import React, { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  LayoutDashboard, Users, UserPlus, CalendarClock, ListChecks,
  PieChart, FileText, BellRing, Settings, LogOut, MessageSquare,
  ClipboardList, Menu, X, Receipt, Sparkles, ScanLine, Bot, Wand2, Repeat, Wrench, ChevronDown, Camera, Fingerprint, Sun, Contact, BookOpen, Building2, TrendingDown, Ticket
} from "lucide-react";
import NotificationBell from "./NotificationBell";
import ChatWidget from "./ChatWidget";
import TaskReminder from "./TaskReminder";
import { installAudioUnlock } from "../lib/sound";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(loc.pathname.startsWith("/tools"));
  const [brand, setBrand] = useState({ company_name: "Spark Holidays", logo: null });

  useEffect(() => {
    api.get("/settings/public").then(r => setBrand(r.data)).catch(()=>{});
    // Enable notification/chat sounds (unlocks audio on first user gesture — required on mobile).
    installAudioUnlock();
  }, []);

  const isAdmin = user?.role === "admin";
  const menu = [
    { to: `/${user?.role}/dashboard`, icon: LayoutDashboard, label: "Dashboard" },
    { to: "/my-day", icon: Sun, label: "My Day" },
    { to: "/leads", icon: ListChecks, label: "Leads" },
    { to: "/leads/new", icon: UserPlus, label: "Add Lead" },
    { to: "/follow-ups", icon: CalendarClock, label: "Follow-ups" },
    { to: "/tasks", icon: ClipboardList, label: "Tasks" },
    { to: "/invoices", icon: Receipt, label: "Invoices" },
    { to: "/itineraries", icon: Sparkles, label: "Itineraries" },
    ...(isAdmin ? [{ to: "/users", icon: Users, label: "Users" }] : []),
    ...(isAdmin ? [{ to: "/analytics", icon: PieChart, label: "Analytics" }] : []),
    { to: "/work-reports", icon: FileText, label: "Work Reports" },
    { to: "/notifications", icon: BellRing, label: "Notifications" },
    ...(isAdmin ? [{ to: "/settings", icon: Settings, label: "Settings" }] : []),
  ];

  const tools = [
    { to: "/tools/arjun-ai", icon: Wand2, label: "Arjun AI" },
    { to: "/tools/rate-tracker", icon: TrendingDown, label: "Rate Tracker" },
    { to: "/tools/passport-vault", icon: ScanLine, label: "Passport Vault" },
    { to: "/tools/aadhaar-scanner", icon: Fingerprint, label: "Aadhaar Scanner" },
    { href: "/voucher-generator.html", external: true, icon: Ticket, label: "Voucher Generator" },
  ];

  const onLogout = () => { logout(); nav(`/${user?.role || "admin"}/login`); };

  return (
    <div className="min-h-screen flex bg-[#FDFDFD]">
      {/* Mobile backdrop */}
      {open && <div data-testid="sidebar-backdrop" onClick={() => setOpen(false)} className="fixed inset-0 bg-black/40 z-40 lg:hidden" />}

      {/* Sidebar */}
      <aside data-testid="sidebar" className={`sidebar-dark fixed lg:sticky top-0 left-0 h-screen w-64 flex-shrink-0 z-50 transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 flex items-center gap-3 border-b border-white/10">
          {brand.logo ? (
            <img src={brand.logo} alt="logo" className="h-10 w-10 rounded-lg object-contain bg-white p-0.5 flex-shrink-0" />
          ) : (
            <div className="h-9 w-9 rounded-lg bg-[#4f87ff] flex items-center justify-center font-bold text-white" style={{fontFamily:'Outfit'}}>S</div>
          )}
          <div>
            <div className="text-white font-semibold" style={{fontFamily:'Outfit'}}>{brand.company_name || "Spark Holidays"}</div>
            <div className="text-[11px] uppercase tracking-widest text-white/40">CRM</div>
          </div>
          <button data-testid="sidebar-close" className="ml-auto lg:hidden text-white/70" onClick={()=>setOpen(false)}><X size={18}/></button>
        </div>
        <nav className="px-3 py-4 space-y-1 overflow-y-auto h-[calc(100vh-170px)]">
          {menu.map(m => {
            const active = loc.pathname === m.to || (m.to !== "/" && loc.pathname.startsWith(m.to) && m.to !== "/leads/new" && !(m.to === "/leads" && loc.pathname === "/leads/new"));
            return (
              <Link key={m.to} to={m.to} onClick={()=>setOpen(false)}
                data-testid={`nav-${m.label.toLowerCase().replace(/\s+/g,'-')}`}
                className={`sidebar-link ${active ? "active" : ""}`}>
                <m.icon size={18} strokeWidth={1.8}/>
                <span>{m.label}</span>
              </Link>
            );
          })}

          {/* Useful Tools section (collapsible — click to expand) */}
          <button
            type="button"
            data-testid="nav-useful-tools"
            onClick={() => setToolsOpen(o => !o)}
            className={`sidebar-link w-full mt-3 ${loc.pathname.startsWith("/tools") ? "active" : ""}`}>
            <Wrench size={18} strokeWidth={1.8}/>
            <span>Useful Tools</span>
            <ChevronDown size={16} strokeWidth={2} className={`ml-auto transition-transform duration-200 ${toolsOpen ? "rotate-180" : ""}`}/>
          </button>
          {toolsOpen && (
            <div className="mt-0.5 ml-3 pl-2 border-l border-white/10 space-y-1">
              {tools.map(m => {
                const active = m.to && (loc.pathname === m.to || loc.pathname.startsWith(m.to));
                if (m.external) {
                  return (
                    <a key={m.href} href={m.href}
                      data-testid={`nav-${m.label.toLowerCase().replace(/\s+/g,'-')}`}
                      className="sidebar-link">
                      <m.icon size={18} strokeWidth={1.8}/>
                      <span>{m.label}</span>
                    </a>
                  );
                }
                return (
                  <Link key={m.to} to={m.to} onClick={()=>setOpen(false)}
                    data-testid={`nav-${m.label.toLowerCase().replace(/\s+/g,'-')}`}
                    className={`sidebar-link ${active ? "active" : ""}`}>
                    <m.icon size={18} strokeWidth={1.8}/>
                    <span>{m.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/10">
          <button data-testid="logout-btn" onClick={onLogout} className="sidebar-link w-full">
            <LogOut size={18} strokeWidth={1.8}/>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-30 h-16 bg-white/85 backdrop-blur border-b border-gray-200 flex items-center px-4 lg:px-8 gap-4">
          <button data-testid="sidebar-toggle" className="lg:hidden text-gray-700" onClick={()=>setOpen(true)}><Menu size={20}/></button>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-wider text-gray-400">Welcome back</div>
            <div className="text-sm font-medium text-gray-900" style={{fontFamily:'Outfit'}}>{user?.name}</div>
          </div>
          <NotificationBell />
          <Link to="/profile" data-testid="topbar-profile" className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100">
            <div className="h-8 w-8 rounded-full bg-[#EEF2FF] text-[#4f87ff] flex items-center justify-center font-semibold">
              {user?.avatar ? <img src={user.avatar} alt="avatar" className="h-8 w-8 rounded-full object-cover"/> : (user?.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="hidden sm:block">
              <div className="text-sm font-medium text-gray-900">{user?.name}</div>
              <div className="text-[11px] text-gray-500 capitalize">{user?.role}</div>
            </div>
          </Link>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 fade-in flex-1">
          {children}
        </main>
      </div>

      <ChatWidget />
      <TaskReminder />
    </div>
  );
}
